import { TenantConfigViewItem } from 'src/dao/tenantConfigClient';
import { OptionGroup, Option } from 'src/components/Configure/ConfigureModal';
import { pick, compact, cloneDeep, isNil } from 'lodash';
import { Indexable } from 'src/types/Primitive';

export interface ParseResult {
  optionGroups: OptionGroup[];
  defaultSelections: Option[];
  originalDefaultSelections: Option[];
  instructions?: string;
  minimumSelections?: number;
  includeAggBys: boolean;
}

export interface Parseable {
  main?: {
    info?: string;
    minimumSelections?: number;
    includeAggBys?: boolean;
  };
  defaults?: Indexable;
  view?: TenantConfigViewItem[];
  defaultsOverride?: Option[];
}

export function parseConfigureConfig(config: Parseable, defaults?: Indexable): ParseResult {
  const optionGroupConfigs = config.view || [];
  const defaultLookup = defaults || config.defaults || {};
  const defaultOptions = {
    dataIndex: '',
    text: '',
  };
  // Configure Grouping
  const optionGroups = optionGroupConfigs.map((group) => ({
    text: group.text,
    options: group.view || [defaultOptions],
  }));

  let defaultSelections = compact(
    optionGroupConfigs.map((group) => {
      const defaultKey = defaultLookup[group.dataIndex];

      if (defaultKey && group.view) {
        for (const child of group.view) {
          if (child.dataIndex === defaultKey) {
            // types Option and TenantConfigViewItem do not overlap quite right
            // coercing to Option to prevent mismatch type issues related to optional text properties
            const picked = pick(child, 'dataIndex', 'text', 'renderer') as Option;
            return picked;
          }
        }
        throw new Error(`Default config item "${defaultKey}" not found in children`);
      }
      return undefined;
    }),
  );
  const originalDefaultSelections = cloneDeep(defaultSelections);
  if (config.defaultsOverride) {
    defaultSelections = config.defaultsOverride;
  }

  return {
    instructions: config.main && config.main.info,
    minimumSelections: config.main && config.main.minimumSelections,
    includeAggBys: config.main?.includeAggBys ?? false,
    optionGroups,
    defaultSelections,
    originalDefaultSelections,
  };
}

/**
 * Saved selections (favorites) are one option per level, in order. If the viewdefn changed
 * and a level's saved option is gone, use that level's current default instead. If there's no
 * default for it either, just stop there - cant skip a level and still fill in the ones after it.
 */
export function reconcileConfigureSelections(
  selections: Option[] | undefined,
  optionGroups: OptionGroup[],
  defaultSelections: Option[],
): Option[] {
  if (isNil(selections)) return defaultSelections;

  const reconciled: Option[] = [];
  for (let i = 0; i < selections.length; i++) {
    const isStillValid = optionGroups[i]?.options.some((opt) => opt.dataIndex === selections[i].dataIndex);
    const resolvedSelection = isStillValid ? selections[i] : defaultSelections[i];
    // no default for this level either - stop, so we don't skip ahead and leave a gap
    if (isNil(resolvedSelection)) break;
    reconciled.push(resolvedSelection);
  }
  return reconciled;
}
